import { isClient } from '@/app/_utils/env';
import { waitToBeOnline } from '@/app/_utils/navigator';

type EventHandler = (ev: unknown) => void | Promise<void>;

// If too much information is stored in localStorage, a memory error can occur.
const MAX_STORED_EVENTS = 100;
// A handheld inside a building entrance hall is offline for minutes at a time, so a failed send is
// retried instead of dropped. The cap stops a permanently failing handler from blocking the queue.
const MAX_SEND_ATTEMPTS = 3;
const RETRY_DELAY_MS = 5000;

/**
 * This file stores events in localStorage and sends them when the browser is online.
 * Events of each type are stored in a different property in localStorage: analyticsEvents: [event1, ...], sentryEvents: [event1, ...]
 * To send the events, it uses the `handler` method.
 */
export const sendNewEvent = (key: string, ev: unknown, handler: EventHandler): void => {
  if (isClient()) {
    const events = getStoredEvents(key);
    if (events.length < MAX_STORED_EVENTS) {
      events.push(ev);
    }
    setStoredEvents(key, events);
    sendStoredEvents(key, handler);
  } else {
    handler(ev);
  }
};

const getStoredEvents = (key: string): unknown[] => {
  let events: unknown[] = [];
  try {
    events = JSON.parse(localStorage.getItem(`${key}Events`) || '[]');
  } catch {
    events = [];
  }
  return events;
};

const setStoredEvents = (key: string, events: unknown[]) => {
  localStorage.setItem(`${key}Events`, JSON.stringify(events));
};

const isSendingEvent = {}; // { analytics: boolean, sentry: boolean, ... }
const failedAttempts = {}; // { analytics: number, sentry: number, ... }

/**
 * Never rejects: it is called without `await`, so a rejection would surface as an unhandled
 * rejection, reach the global error listeners and be reported as an error — which is how the
 * reporting path started feeding itself (CBX-11201).
 */
const sendStoredEvents = async (key: string, handler: EventHandler) => {
  try {
    const events = getStoredEvents(key);

    if (!isSendingEvent[key] && events.length > 0) {
      let retryDelay = 0;
      let firstEvent: unknown;
      try {
        isSendingEvent[key] = true;

        // Get first event and store the remaining events
        firstEvent = events.shift();
        setStoredEvents(key, events);

        // Send first event
        await waitToBeOnline();
        await handler(firstEvent);
        failedAttempts[key] = 0;
      } catch {
        retryDelay = scheduleRetry(key, firstEvent);
      }
      isSendingEvent[key] = false;

      // Send the remaining events
      setTimeout(() => sendStoredEvents(key, handler), retryDelay);
    }
  } catch {
    isSendingEvent[key] = false;
  }
};

/**
 * Puts a failed event back at the front of the queue and returns how long to wait before the next
 * attempt. Once an event has failed MAX_SEND_ATTEMPTS times it is dropped, so the queue keeps
 * draining even when the handler is permanently broken.
 */
const scheduleRetry = (key: string, ev: unknown): number => {
  failedAttempts[key] = (failedAttempts[key] || 0) + 1;
  if (failedAttempts[key] >= MAX_SEND_ATTEMPTS) {
    failedAttempts[key] = 0;
    return 0;
  }
  try {
    const events = getStoredEvents(key);
    events.unshift(ev);
    setStoredEvents(key, events.slice(0, MAX_STORED_EVENTS));
  } catch {
    // localStorage is unavailable or full: drop the event rather than fail the queue.
  }
  return RETRY_DELAY_MS;
};
